import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { albaranes } from '@/utils/albaranes'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useQuerySync from '@/utils/useQuerySync'

export default function useAlbaranesList() {
  // Use toast
  const toast = useToast()

  const refAlbaranesListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'codigo', label: 'Código', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true },
    { key: 'pedido_codigo', label: 'Nº Pedido', sortable: true, thStyle: { minWidth: "200px" } },
    { key: 'estado', label: 'Estado', sortable: true },
    { key: 'user_creado.name', label: 'Creado por', sortable: true },
    { key: 'empleado_recoge_nombre', label: 'Entregado a', sortable: false },
    { key: 'nota', label: 'Nota', sortable: true },
    { key: 'actions', label: 'Acciones', thStyle: { minWidth: "150px" } },
  ]
  const perPage = ref(50)
  const totalAlbaranes = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const statusFilter = ref(null)
  const fechaDesde = ref(null)
  const fechaHasta = ref(null)

  useQuerySync({
    search: searchQuery,
    status: statusFilter,
    fecha_desde: fechaDesde,
    fecha_hasta: fechaHasta,
    page: currentPage,
    per_page: perPage,
  })

  const dataMeta = computed(() => {
    const localItemsCount = refAlbaranesListTable.value ? refAlbaranesListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalAlbaranes.value,
    }
  })

  const refetchData = () => {
    refAlbaranesListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, statusFilter, fechaDesde, fechaHasta], () => {
    refetchData()
  })

  const clearFilters = () => {
    searchQuery.value = ''
    statusFilter.value = null
    fechaDesde.value = null
    fechaHasta.value = null
    currentPage.value = 1
    refetchData()
  }

  const fetchAlbaranes = (ctx, callback) => {
    store
      .dispatch('app-albaranes/fetchAlbaranes', {
        body: {
          search: searchQuery.value,
          paginate: perPage.value,
          user_created: store.state.auth.user.fk_role !== 1 ? store.state.auth.user.id : null,
          estado: statusFilter.value,
          fecha_desde: fechaDesde.value,
          fecha_hasta: fechaHasta.value,
        },
        headers: {
          'X-Sort': `albaranes.${sortBy.value}`,
          'X-Sort-Order': isSortDirDesc.value ? 'desc' : 'asc',
        },
        query: {
          paginate: true,
          page: currentPage.value,
        },
      })
      .then(res => {
        const { total } = res
        totalAlbaranes.value = total
        callback(res.data)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching albaranes list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // Devuelve TODOS los albaranes del filtro actual (sin paginar) — para exportar.
  const fetchAllFiltered = () => store
    .dispatch('app-albaranes/fetchAlbaranes', {
      body: {
        search: searchQuery.value,
        paginate: 100000,
        user_created: store.state.auth.user.fk_role !== 1 ? store.state.auth.user.id : null,
        estado: statusFilter.value,
        fecha_desde: fechaDesde.value,
        fecha_hasta: fechaHasta.value,
      },
      headers: {
        'X-Sort': `albaranes.${sortBy.value}`,
        'X-Sort-Order': isSortDirDesc.value ? 'desc' : 'asc',
      },
      query: { paginate: true, page: 1 },
    })
    .then(res => (res && res.data ? res.data : []))

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const parseDate = date => {
    let d = date.substring(0, 10)
    d = d.split('-').reverse().join('-')
    const t = date.substring(12, 19)
    return `${d} ${t}`
  }

  const resolveStatusVariant = status => {
    const item = albaranes.estados.find((el) => el.value === status)
    if (item) return item.variant
    return '-'
  }

  const resolveStatusText = status => {
    const item = albaranes.estados.find((el) => el.value === status)
    if (item) return item.label
    return '-'
  }

  // Columnas exportables (sin "Acciones"), con formato para celdas derivadas.
  const exportColumns = [
    { key: 'codigo', label: 'Código' },
    { key: 'fecha', label: 'Fecha', format: r => (r.fecha ? String(r.fecha).substring(0, 10).split('-').reverse().join('-') : '') },
    { key: 'pedido_codigo', label: 'Nº Pedido' },
    { key: 'estado', label: 'Estado', format: r => resolveStatusText(r.estado) },
    { key: 'user_creado', label: 'Creado por', format: r => (r.user_creado ? r.user_creado.name : '') },
    { key: 'empleado_recoge_nombre', label: 'Entregado a' },
    { key: 'nota', label: 'Nota' },
  ]

  return {
    fetchAlbaranes,
    tableColumns,
    perPage,
    currentPage,
    totalAlbaranes,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refAlbaranesListTable,

    refetchData,
    parseDate,
    resolveStatusVariant,
    resolveStatusText,

    // Extra Filters
    statusFilter,
    fechaDesde,
    fechaHasta,
    clearFilters,

    // Export
    exportColumns,
    fetchAllFiltered,
  }
}
